import React, { useState, useEffect } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { Form, Button, Card, Container, Alert } from 'react-bootstrap';
import { authService } from '../../services/authService';
import './LoginPage.css';

interface PasswordFormData {
    password: string;
    confirmPassword: string;
}

const RecuperarSenhaPage: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const [formData, setFormData] = useState<PasswordFormData>({
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [tokenValid, setTokenValid] = useState<boolean>(false);
    const [validatingToken, setValidatingToken] = useState<boolean>(true);
    const [token, setToken] = useState<string>('');
    const [userInfo, setUserInfo] = useState<{ email: string; nome: string } | null>(null);

    // Function to get token from URL query parameters
    const getTokenFromUrl = (): string | null => {
        const urlParams = new URLSearchParams(location.search);
        return urlParams.get('token');
    };

    // Validate token using backend API
    const validateToken = async (token: string) => {
        try {
            setValidatingToken(true);
            setError('');

            // Call the backend API to validate the token
            const response = await authService.validarTokenRecuperacao(token);

            if (response.valid) {
                // Token is valid, store user information
                setTokenValid(true);
                setToken(token);
                if (response.email && response.nome) {
                    setUserInfo({
                        email: response.email,
                        nome: response.nome
                    });
                }
            } else {
                // Token is invalid
                setError(response.message || 'Token inválido ou expirado');
                setTokenValid(false);
            }

        } catch (err) {
            console.error('Error validating token:', err);
            setError('Erro ao validar token. Por favor, tente novamente.');
            setTokenValid(false);
        } finally {
            setValidatingToken(false);
        }
    };

    useEffect(() => {
        const tokenFromUrl = getTokenFromUrl();
        if (!tokenFromUrl) {
            setError('Token não fornecido na URL. Por favor, use o link enviado por email.');
            setValidatingToken(false);
            return;
        }
        validateToken(tokenFromUrl);
    }, [location.search]);

    const validatePassword = (password: string): boolean => {
        // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        return passwordRegex.test(password);
    };

    // Individual password requirement validation functions
    const validateMinLength = (password: string): boolean => {
        return password.length >= 8;
    };

    const validateUppercase = (password: string): boolean => {
        return /[A-Z]/.test(password);
    };

    const validateLowercase = (password: string): boolean => {
        return /[a-z]/.test(password);
    };

    const validateNumber = (password: string): boolean => {
        return /\d/.test(password);
    };

    const validateSpecialChar = (password: string): boolean => {
        return /[@$!%*?&]/.test(password);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validate password
        if (!validatePassword(formData.password)) {
            setError('A senha deve conter pelo menos 8 caracteres, incluindo letra maiúscula, minúscula, número e caractere especial');
            return;
        }

        // Check if passwords match
        if (formData.password !== formData.confirmPassword) {
            setError('As senhas não coincidem');
            return;
        }

        setLoading(true);

        try {
            // Call the API to update password using magic link token
            await authService.atualizarSenhaComToken(token, formData.password);

            setSuccess('Senha atualizada com sucesso!');
            // Don't auto-redirect, let user click button instead
        } catch (err: any) {
            console.error('Error updating password:', err);

            // Extract error message from backend response
            let errorMessage = 'Erro ao atualizar senha. Por favor, tente novamente.';

            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (validatingToken) {
        return (
            <Container className="d-flex align-items-center justify-content-center min-vh-100">
                <Card className="login-card">
                    <Card.Body className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                        <p className="mt-3">Validando token...</p>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    if (!tokenValid) {
        return (
            <Container className="d-flex align-items-center justify-content-center min-vh-100">
                <Card className="login-card">
                    <Card.Body>
                        <Card.Title className="text-center mb-4">Erro</Card.Title>
                        <Alert variant="danger">
                            <div className="mb-3">{error || 'Token inválido ou expirado'}</div>
                            <div className="d-flex justify-content-center">
                                <Link to="/esqueci-senha" className="btn btn-outline-danger btn-sm">
                                    Solicitar novo link de recuperação
                                </Link>
                            </div>
                        </Alert>
                        <div className="text-center mt-3">
                            <Link to="/esqueci-senha" className="btn btn-primary">
                                Solicitar novo link
                            </Link>
                        </div>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <Container className="d-flex align-items-center justify-content-center min-vh-100">
            <Card className="login-card">
                <Card.Body>
                    <Card.Title className="text-center mb-4">Recuperar Senha</Card.Title>

                    {/* Display user information if available */}
                    {userInfo && (
                        <Alert variant="info" className="mb-3">
                            <div className="text-center">
                                <strong>Olá, {userInfo.nome}!</strong><br />
                                <small className="text-muted">{userInfo.email}</small>
                            </div>
                        </Alert>
                    )}

                    {error && (
                        <Alert variant="danger">
                            <div className="mb-3">{error}</div>
                            <div className="d-flex justify-content-center">
                                <Link to="/esqueci-senha" className="btn btn-outline-danger btn-sm">
                                    Solicitar novo link de recuperação
                                </Link>
                            </div>
                        </Alert>
                    )}
                    {success && (
                        <Alert variant="success">
                            <div className="mb-3">{success}</div>
                            <div className="d-flex justify-content-center">
                                <Button
                                    variant="outline-success"
                                    onClick={() => history.push('/login')}
                                    className="btn-sm"
                                >
                                    Ir para o Login
                                </Button>
                            </div>
                        </Alert>
                    )}
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Nova Senha</Form.Label>
                            <Form.Control
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Digite sua nova senha"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Confirmar Nova Senha</Form.Label>
                            <Form.Control
                                type="password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                placeholder="Confirme sua nova senha"
                                required
                            />
                        </Form.Group>

                        <div className="mb-4">
                            <div className="card border-info">
                                <div className="card-header bg-light text-dark">
                                    <i className="bi bi-info-circle me-2"></i>
                                    Requisitos da Senha
                                </div>
                                <div className="card-body">
                                    <ul className="list-unstyled mb-0">
                                        <li className="mb-2">
                                            <i className={`bi ${validateMinLength(formData.password) ? 'bi-check-circle-fill text-success' : 'bi-circle text-muted'} me-2`}></i>
                                            Mínimo de 8 caracteres
                                        </li>
                                        <li className="mb-2">
                                            <i className={`bi ${validateUppercase(formData.password) ? 'bi-check-circle-fill text-success' : 'bi-circle text-muted'} me-2`}></i>
                                            Pelo menos uma letra maiúscula
                                        </li>
                                        <li className="mb-2">
                                            <i className={`bi ${validateLowercase(formData.password) ? 'bi-check-circle-fill text-success' : 'bi-circle text-muted'} me-2`}></i>
                                            Pelo menos uma letra minúscula
                                        </li>
                                        <li className="mb-2">
                                            <i className={`bi ${validateNumber(formData.password) ? 'bi-check-circle-fill text-success' : 'bi-circle text-muted'} me-2`}></i>
                                            Pelo menos um número
                                        </li>
                                        <li className="mb-0">
                                            <i className={`bi ${validateSpecialChar(formData.password) ? 'bi-check-circle-fill text-success' : 'bi-circle text-muted'} me-2`}></i>
                                            Pelo menos um caractere especial (@$!%*?&)
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <Button
                            variant="primary"
                            type="submit"
                            className="w-100"
                            disabled={loading}
                        >
                            {loading ? 'Atualizando...' : 'Atualizar Senha'}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default RecuperarSenhaPage;
